import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from '../../utils/incentiveChallengeApi';

/**
 * Incentive challenges — Finance's CRUD, against the real API.
 *
 * The list endpoint carries each challenge's progress with it (the table shows
 * achievers per row), so one request fills the whole page.
 */

const initialState = {
  list: [],
  loading: false,
  saving: false,
  /**
   * The filters the list was last fetched with. Filtering is server-side, so
   * the progress behind each row is only worked out for challenges in view —
   * and a save or delete elsewhere refetches with these rather than reloading
   * every challenge ever made.
   */
  filters: {},
  /** Only the newest list request may fill the table — filters change quickly. */
  latestRequestId: null,
};

/** Called with filters to change them, or with none to refetch the current view. */
export const getChallenges = createAsyncThunk('challenges/list', async (filters, { getState }) =>
  api.listChallenges(filters ?? getState().challenges.filters),
);

export const createChallenge = createAsyncThunk('challenges/create', async (values) =>
  api.createChallenge(values),
);

export const updateChallenge = createAsyncThunk('challenges/update', async ({ id, values }) =>
  api.updateChallenge(id, values),
);

export const removeChallenge = createAsyncThunk('challenges/remove', async (id) =>
  api.deleteChallenge(id),
);

const challengeSlice = createSlice({
  name: 'challenges',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getChallenges.pending, (state, action) => {
        state.loading = true;
        state.latestRequestId = action.meta.requestId;
        if (action.meta.arg) state.filters = action.meta.arg;
      })
      .addCase(getChallenges.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.loading = false;
        state.list =
          action.payload?.status === 'success'
            ? (action.payload.data || []).map(api.toChallenge)
            : [];
      })
      .addCase(getChallenges.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.loading = false;
        state.list = [];
      });

    // Writes only need "is something in flight" — the list is refetched
    // afterwards rather than patched, so the table can never drift from what
    // the server would return.
    [createChallenge, updateChallenge, removeChallenge].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state) => {
          state.saving = false;
        });
    });
  },
});

export default challengeSlice.reducer;
